/* =====================================================================
   AstaLab 2.0 — /catalog (round alpha.2, sezioni 2, 5, 6, 8, 12)

   Pagina UNICA, condivisa BIT-A-BIT fra Artifact e deploy reale (nessuna variante scritta qui): la
   sola differenza è quale CatalogRepository ctx.catalogRepository incapsula (Artifact* locale o
   Supabase* reale, vedi backend/bootstrap.js) — questa pagina chiama SOLO l'interfaccia astratta
   CatalogRepository, mai un dettaglio di implementazione dei due backend.

   Copre le funzioni dimostrabili nell'Artifact richieste esplicitamente dalla sezione 12: import
   listone, aggiornamento del listone, import statistiche, copertura multistagione, nuovi/usciti,
   scheda giocatore (con statistiche e associazioni esterne). Import globale = admin-only sul deploy
   reale (sezione 9): se una RPC rifiuta l'operazione, il messaggio di errore reale del backend viene
   mostrato cosi' com'è, mai mascherato da un falso successo.
   ===================================================================== */
import { escapeHtml } from "../components/escapeHtml.js";
import { buildListoneUpdatePreview, buildStatisticsImportPreview } from "../../catalog/importPreview.js";
import { externalIdEntries, isVendorScopedExternalSource } from "../../catalog/externalRecordsEngine.js";
import { ROLES } from "../../engine/astalabEngine.js";
import { logClientError } from "../../utils/clientLogger.js";

// Round A7-P1-4 — vocabolario ruoli riusato VERBATIM da astalabEngine.js (mai un alias/bucket
// "sconosciuto" inventato qui: il modello dati garantisce già, a tre livelli indipendenti — CHECK
// di schema, validazione RPC, parser strict lato import — che p.ruolo sia sempre esattamente uno dei
// quattro valori di ROLES). "ALL" è un quinto valore puramente di UI, mai scritto su alcun player.
const ROLE_FILTER_OPTIONS = ["ALL", ...ROLES];
const ROLE_FILTER_LABELS = { ALL: "Tutti", POR: "POR", DIF: "DIF", CEN: "CEN", ATT: "ATT" };

// Round Beta-P2-1 — "Load more": stessa dimensione già usata dal cap esistente, ora anche
// l'incremento di rivelazione per ogni click. view.catalogVisibleCount (sotto) resta l'UNICO
// meccanismo di paginazione — un valore di stato locale alla pagina, mai un offset/cursore
// lato repository/backend (listCurrentPlayers() non ha e non riceve alcun limite esplicito:
// vedi il commento su refreshAll() più sotto).
const CATALOG_PAGE_SIZE = 50;

const EXTERNAL_SOURCES = [
  { value: "transfermarkt-manual", label: "Transfermarkt (manuale)" },
  { value: "csv-manual", label: "CSV manuale" },
  { value: "provider-authorized", label: "Provider autorizzato" }
];

function fmtDate(iso){
  if(!iso) return "—";
  try{ return new Date(iso).toLocaleString("it-IT"); }catch(e){ return iso; }
}
async function fileToBytes(file){
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

/** Un esempio reale di giocatore per ciascuna categoria di copertura (in entrambe le stagioni,
 *  solo una, in nessuna) — non solo il conteggio aggregato: rende la copertura verificabile a
 *  colpo d'occhio (e verificabile in un test browser reale, non solo nel motore). Itera l'intero
 *  catalogo corrente finché non trova un esempio per ogni categoria (mai un campione parziale che
 *  rischierebbe di non trovarne uno). */
async function computeCoverageExamples(ctx, players, seasons){
  const examples = { both: null, none: null };
  seasons.forEach(sk => { examples["only_" + sk] = null; });
  const stillMissing = () => examples.both == null || examples.none == null || seasons.some(sk => examples["only_" + sk] == null);
  for(const p of players){
    if(!stillMissing()) break;
    const present = [];
    for(const sk of seasons){
      const stats = await ctx.catalogRepository.getPlayerSeasonStats(p.id, sk);
      if(stats) present.push(sk);
    }
    if(present.length === seasons.length && examples.both == null) examples.both = p.nome;
    else if(present.length === 0 && examples.none == null) examples.none = p.nome;
    else if(present.length === 1 && examples["only_" + present[0]] == null) examples["only_" + present[0]] = p.nome;
  }
  return examples;
}

// Round Beta-P3-7 (design D/D2, accettato): contatore di generazione module-private, incrementato
// come PRIMISSIMA istruzione di OGNI render completo di questa pagina — mai esposto pubblicamente.
// Stesso identico principio/motivazione di _dashboardRenderGeneration in DashboardPage.js: protegge
// il fallback secondario più sotto da un rientro sulla STESSA route dopo che un render più recente
// ha già vinto (hash identico, ma un tentativo di render completamente diverso — vedi P3-7D2 §1).
let _catalogRenderGeneration = 0;

export async function renderCatalogPage(container, ctx){
  _catalogRenderGeneration += 1;
  // Round alpha.7 (A7-P0-2): data-catalog-state è il contratto di readiness strutturale della
  // pagina — "loading"/"ready"/"error" su QUESTO elemento radice, mai dedotto da testo prosa
  // italiano (che può cambiare, essere localizzato, o richiedere tempi diversi da un ambiente
  // all'altro) né dalla sola presenza di un container generico (vedi ALPHA6-R2 D3B/D3C: un harness
  // di accettazione ha fallito due volte proprio perché aspettava .catalog, presente PRIMA che il
  // fetch dei 504 giocatori reali fosse anche solo iniziato — quel placeholder ora porta
  // esplicitamente "loading"). "ready" significa: il risultato corrente del catalogo (popolato O
  // vuoto — un catalogo vuoto è comunque un caricamento COMPLETATO, mai "loading" perenne) è stato
  // dipinto e i controlli interattivi normali della pagina sono utilizzabili. "error" e "ready" non
  // sono mai scritti insieme: sono percorsi di innerHTML del container mutuamente esclusivi.
  container.innerHTML = '<div class="catalog" data-catalog-state="loading"><p class="hint">Caricamento catalogo…</p></div>';
  const hashAtEntry = window.location.hash;
  // Round Beta-P3-4 (design D/D2, accettato): catch NARROW, dedicato SOLO a getCurrentUser() — mai
  // condiviso con refreshAll() sotto, la cui semantica di fallimento (renderCatalogLoadError, sotto,
  // invariata) resta un problema distinto. Un rigetto qui è un'incertezza sulla sessione (vedi
  // BackendAdapter.js/SupabaseBackendAdapter.js) — mai un redirect a /login, una card di retry
  // veritiera invece, senza mai lasciare il vecchio DOM della route precedente visibile.
  let user;
  try{
    user = await ctx.adapter.getCurrentUser();
  }catch(err){
    if(window.location.hash !== hashAtEntry) return;
    container.innerHTML = renderCatalogAuthUncertain();
    wireCatalogAuthUncertain(container, ctx);
    return;
  }
  if(window.location.hash !== hashAtEntry) return;
  if(!user){ ctx.navigate("/login"); return; }

  const view = {
    players: [], coverage: null, coverageExamples: {}, targetSeasons: [],
    listonePreview: null, lastListoneSummary: null,
    statsPreview: null, lastStatsSummary: null,
    playerFilter: "", roleFilter: "ALL", catalogVisibleCount: CATALOG_PAGE_SIZE,
    selectedPlayerId: null, selectedPlayerDetail: null, selectedPlayerStats: {},
    selectedPlayerExternalStats: [], selectedPlayerQueueStats: [],
    // Round 2.2.0 (risoluzione dei conflitti statistici UFFICIALI): isAdmin è SOLO una comodità UI
    // (mostra/nasconde questa sezione) — mai il confine di sicurezza reale, che resta sempre
    // private.is_admin() dentro resolve_statistics_conflict() lato RPC (vedi
    // CatalogRepository.isCurrentUserAdmin()).
    isAdmin: false, officialStatisticsConflicts: [],
    msg: { listone: "", stats: "", coverage: "", player: "", external: "", conflicts: "" }
  };

  // Round 2.2.0: refresh mirato, mai l'intero refreshAll() — una risoluzione riuscita/fallita non
  // deve mai forzare un ricaricamento ridondante di giocatori/copertura/stagioni target, che quella
  // mutazione non tocca mai.
  async function refreshConflicts(){
    if(!view.isAdmin) return;
    view.officialStatisticsConflicts = await ctx.catalogRepository.listOfficialStatisticsConflicts();
  }

  async function refreshAll(){
    // Round Beta-P2-1: listCurrentPlayers() non impone né riceve alcun limite esplicito —
    // restituisce oggi l'intero catalogo corrente, come già faceva prima di questo round (nessuna
    // garanzia di "risultato lato server sempre illimitato", solo l'assenza di un limite applicativo
    // qui) — vedi CATALOG_PAGE_SIZE sopra per l'unico punto in cui un limite viene ora imposto,
    // esclusivamente lato UI. Un catalogo sostituito/ricaricato è sempre un universo di browse
    // NUOVO: la finestra visibile riparte da CATALOG_PAGE_SIZE, mai portata avanti dal precedente.
    view.players = await ctx.catalogRepository.listCurrentPlayers();
    view.targetSeasons = await ctx.catalogRepository.getStatisticsTargetSeasons();
    view.catalogVisibleCount = CATALOG_PAGE_SIZE;
    view.isAdmin = await ctx.catalogRepository.isCurrentUserAdmin();
    view.officialStatisticsConflicts = view.isAdmin ? await ctx.catalogRepository.listOfficialStatisticsConflicts() : [];
  }
  try{
    await refreshAll();
  }catch(e){
    // Percorso PRIMA inesistente: un rifiuto qui non veniva mai intercettato, mai reso in pagina —
    // ora è un terzo stato osservabile esplicito, mai simultaneo con "ready".
    container.innerHTML = renderCatalogLoadError(e);
    return;
  }

  function paint(){
    container.innerHTML = renderShell(view);
    wire();
  }

  async function selectPlayer(playerId){
    view.selectedPlayerId = playerId;
    view.selectedPlayerDetail = await ctx.catalogRepository.getPlayer(playerId);
    view.selectedPlayerStats = {};
    for(const sk of view.targetSeasons){
      view.selectedPlayerStats[sk] = await ctx.catalogRepository.getPlayerSeasonStats(playerId, sk);
    }
    // Record ESTERNI: separati dalle associazioni manuali (externalIds) — questi sono le righe
    // statistiche di externalStatistics già risolte per fantacalcioId (attive o in attesa di
    // selezione esplicita, mai i record ancora in coda "non associati" — quelli non riguardano
    // ancora nessun giocatore specifico).
    const detail = view.selectedPlayerDetail;
    const allExternal = detail ? await ctx.catalogRepository.listExternalRecords() : [];
    view.selectedPlayerExternalStats = detail ? allExternal.filter(r => r.fantacalcioId === detail.fantacalcioId) : [];
    // Coda "non risolti" (obiettivo 8/F): un record qui per questo fantacalcioId spiega ESATTAMENTE
    // perché non è (più) attivo — unmatched/ambiguous/identity-conflict, mai un dettaglio nascosto.
    const allQueue = detail ? await ctx.catalogRepository.listUnassociatedExternalRecords() : [];
    view.selectedPlayerQueueStats = detail ? allQueue.filter(r => r.fantacalcioId === detail.fantacalcioId) : [];
    view.msg.player = "";
    view.msg.external = "";
    paint();
  }

  function wire(){
    container.querySelector("#catalogBackLink").addEventListener("click", (e) => { e.preventDefault(); ctx.navigate("/dashboard"); });

    // --- Import listone (obiettivo 2, 5) ---
    const listoneFile = container.querySelector("#listoneFile");
    if(listoneFile) listoneFile.addEventListener("change", async (e) => {
      const file = e.target.files && e.target.files[0];
      if(!file) return;
      const bytes = await fileToBytes(file);
      const preview = buildListoneUpdatePreview(bytes, file.name, file.type);
      if(!preview.ok){ view.msg.listone = preview.error; view.listonePreview = null; paint(); return; }
      view.listonePreview = preview;
      view.msg.listone = "";
      paint();
    });
    const confirmListoneBtn = container.querySelector("#confirmListoneBtn");
    if(confirmListoneBtn) confirmListoneBtn.addEventListener("click", async () => {
      const preview = view.listonePreview;
      if(!preview) return;
      const result = await ctx.catalogRepository.applyListoneSnapshot(preview.records, preview.sourceFile, preview.season, preview.fileChecksum);
      if(!result.ok){ view.msg.listone = result.error; paint(); return; }
      view.lastListoneSummary = result.summary;
      view.listonePreview = null;
      view.msg.listone = "";
      await refreshAll();
      paint();
    });
    const cancelListoneBtn = container.querySelector("#cancelListoneBtn");
    if(cancelListoneBtn) cancelListoneBtn.addEventListener("click", () => { view.listonePreview = null; paint(); });

    // --- Import statistiche (obiettivo 5) ---
    const statsFile = container.querySelector("#statsFile");
    if(statsFile) statsFile.addEventListener("change", async (e) => {
      const file = e.target.files && e.target.files[0];
      if(!file) return;
      const bytes = await fileToBytes(file);
      const preview = buildStatisticsImportPreview(bytes, file.name, file.type);
      if(!preview.ok){ view.msg.stats = preview.error; view.statsPreview = null; paint(); return; }
      view.statsPreview = preview;
      view.msg.stats = "";
      paint();
    });
    const confirmStatsBtn = container.querySelector("#confirmStatsBtn");
    if(confirmStatsBtn) confirmStatsBtn.addEventListener("click", async () => {
      const preview = view.statsPreview;
      if(!preview) return;
      const result = await ctx.catalogRepository.applyStatisticsImport(preview);
      if(!result.ok){ view.msg.stats = result.error; paint(); return; }
      view.lastStatsSummary = result.summary;
      view.statsPreview = null;
      view.msg.stats = "";
      await refreshAll();
      paint();
    });
    const cancelStatsBtn = container.querySelector("#cancelStatsBtn");
    if(cancelStatsBtn) cancelStatsBtn.addEventListener("click", () => { view.statsPreview = null; paint(); });

    // --- Copertura multistagione (obiettivo 6) ---
    const coverageBtn = container.querySelector("#computeCoverageBtn");
    if(coverageBtn) coverageBtn.addEventListener("click", async () => {
      try{
        view.coverage = await ctx.catalogRepository.getCoverage();
        view.coverageExamples = await computeCoverageExamples(ctx, view.players, view.coverage.seasons);
        view.msg.coverage = "";
      }
      catch(e){ view.msg.coverage = String(e && e.message || e); }
      paint();
    });

    // --- Conflitti statistici ufficiali (round 2.2.0, admin-only) ---
    // Nessun controllo isAdmin qui: se la sezione non è renderizzata (non-admin), questi elementi
    // semplicemente non esistono nel DOM — querySelectorAll su un container senza match restituisce
    // sempre una NodeList vuota, mai un errore. Il vero confine di sicurezza resta sempre
    // private.is_admin() dentro la RPC: un non-admin che invocasse comunque resolveStatisticsConflict
    // (bypassando questa UI) riceverebbe comunque un rifiuto server-side.
    container.querySelectorAll("[data-conflict-action]").forEach(btn => {
      btn.addEventListener("click", async () => {
        const conflictId = btn.getAttribute("data-conflict-id");
        const action = btn.getAttribute("data-conflict-action");
        let customValue = null;
        if(action === "custom_value"){
          const input = container.querySelector('[data-conflict-custom-input="' + CSS.escape(conflictId) + '"]');
          const raw = input ? input.value : "";
          if(raw === "" || raw == null){
            view.msg.conflicts = "Inserire un valore personalizzato prima di confermare.";
            paint();
            return;
          }
          customValue = raw;
        }
        const result = await ctx.catalogRepository.resolveStatisticsConflict(conflictId, action, customValue);
        if(!result.ok){
          // Round 2.2.0: mai un successo ottimistico — un fallimento (rifiuto RPC/repository, conflitto
          // obsoleto, valore non valido) lascia la riga esattamente dov'era, mai rimossa/segnata come
          // risolta prima che il repository lo confermi davvero.
          view.msg.conflicts = result.error || "Impossibile risolvere il conflitto.";
          paint();
          return;
        }
        view.msg.conflicts = "";
        await refreshConflicts();
        paint();
      });
    });

    // --- Elenco/ricerca giocatori + scheda giocatore ---
    const filterInput = container.querySelector("#playerFilter");
    if(filterInput) filterInput.addEventListener("input", (e) => {
      view.playerFilter = e.target.value;
      // Round Beta-P2-1: un cambio di ricerca definisce un universo di browse NUOVO — la finestra
      // "Load more" riparte sempre da CATALOG_PAGE_SIZE, mai portata avanti dalla ricerca precedente.
      view.catalogVisibleCount = CATALOG_PAGE_SIZE;
      paint();
      // paint() ricostruisce l'intero container (stesso pattern di ogni altra pagina 2.0), il che
      // distruggerebbe il focus dell'input di ricerca ad ogni carattere digitato: lo ripristiniamo
      // esplicitamente sul nuovo elemento, con il cursore in fondo al testo già digitato.
      const el = container.querySelector("#playerFilter");
      if(el){ el.focus(); const pos = el.value.length; el.setSelectionRange(pos, pos); }
    });
    container.querySelectorAll("[data-select-player]").forEach(el=>{
      el.addEventListener("click", () => selectPlayer(el.getAttribute("data-select-player")));
    });

    // --- Filtro ruolo catalogo (A7-P1-4) ---
    container.querySelectorAll("[data-catalog-role-filter] [data-catalog-role]").forEach(btn => {
      btn.addEventListener("click", () => {
        const role = btn.getAttribute("data-catalog-role");
        if(role === view.roleFilter) return;   // già selezionato: nessun repaint/focus-move superfluo
        view.roleFilter = role;
        // Round Beta-P2-1: stesso principio del filtro testuale sopra — un nuovo universo di browse.
        view.catalogVisibleCount = CATALOG_PAGE_SIZE;
        paint();
        // Amendment obbligatorio A7-P1-4 punto 2: paint() ricostruisce l'intero container, distruggendo
        // il nodo <button> su cui era il focus da tastiera — senza questo, Tab/Enter ripetuti su ruoli
        // diversi perderebbero il focus nel vuoto (document.body) a ogni attivazione. Ripristinato
        // esplicitamente sul NUOVO bottone che ora porta data-catalog-role-selected="true", stesso
        // meccanismo minimo già usato per #playerFilter poco sopra — nessuna infrastruttura di focus
        // globale, solo una query mirata subito dopo paint().
        const newBtn = container.querySelector('[data-catalog-role="' + role + '"]');
        if(newBtn) newBtn.focus();
      });
    });
    const resetFiltersBtn = container.querySelector("#resetCatalogFiltersBtn");
    if(resetFiltersBtn) resetFiltersBtn.addEventListener("click", () => {
      view.playerFilter = "";
      view.roleFilter = "ALL";
      view.catalogVisibleCount = CATALOG_PAGE_SIZE;
      paint();
      const el = container.querySelector("#playerFilter");
      if(el) el.focus();
    });
    // Round Beta-P2-1 — "Load more": nessun nuovo fetch, nessuna mutazione dei filtri, nessun tocco
    // a selectedPlayerId/selectedPlayerDetail. Il pulsante appartiene al DOM VECCHIO: se dopo il
    // repaint hiddenCount è sceso a zero, il pulsante semplicemente non viene più renderizzato — mai
    // un tentativo di focus su un nodo che paint() non ha più ricreato.
    const loadMoreBtn = container.querySelector("[data-catalog-load-more]");
    if(loadMoreBtn) loadMoreBtn.addEventListener("click", () => {
      view.catalogVisibleCount += CATALOG_PAGE_SIZE;
      paint();
      const survivingBtn = container.querySelector("[data-catalog-load-more]");
      if(survivingBtn) survivingBtn.focus();
    });
    const closeCardBtn = container.querySelector("#closePlayerCardBtn");
    if(closeCardBtn) closeCardBtn.addEventListener("click", () => { view.selectedPlayerId = null; view.selectedPlayerDetail = null; paint(); });

    // --- Import CSV esterno + associazioni manuali (obiettivo 8/F della 1.3) ---
    const externalFile = container.querySelector("#externalCsvFile");
    if(externalFile) externalFile.addEventListener("change", async (e) => {
      const file = e.target.files && e.target.files[0];
      if(!file) return;
      const text = await file.text();
      const result = await ctx.catalogRepository.importExternalStatisticsCSV(text, file.name);
      view.msg.external = !result.ok ? result.error
        : result.skipped ? "File già importato in precedenza: nessuna modifica (idempotenza)."
        : "Import esterno completato (" + result.warnings.length + " avvisi di riconciliazione).";
      if(view.selectedPlayerId) await selectPlayer(view.selectedPlayerId); else paint();
    });
    const associateForm = container.querySelector("#associateExternalForm");
    if(associateForm) associateForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const source = container.querySelector("#associateSource").value;
      const sourceId = container.querySelector("#associateSourceId").value;
      const sourceUrl = container.querySelector("#associateSourceUrl").value;
      // Revisione alpha.3-r3 (audit blocker B su r2): providerKey letto dal form SOLO per una fonte
      // vendor-scoped ("provider-authorized") — ignorato/non inviato per le altre, mai un valore
      // fantasma che farebbe rifiutare un'associazione legacy dal backend.
      const providerKeyInput = container.querySelector("#associateProviderKey");
      const providerKey = isVendorScopedExternalSource(source) && providerKeyInput ? providerKeyInput.value : undefined;
      const result = await ctx.catalogRepository.associateExternalId(view.selectedPlayerId, source, sourceId, sourceUrl, providerKey);
      view.msg.external = result.ok ? "" : result.error;
      await selectPlayer(view.selectedPlayerId);
    });
    container.querySelectorAll("[data-remove-external]").forEach(btn=>{
      btn.addEventListener("click", async () => {
        const source = btn.getAttribute("data-remove-external");
        const providerKey = btn.getAttribute("data-remove-external-provider-key") || undefined;
        const result = await ctx.catalogRepository.removeExternalId(view.selectedPlayerId, source, providerKey);
        view.msg.external = result.ok ? "" : result.error;
        await selectPlayer(view.selectedPlayerId);
      });
    });
    container.querySelectorAll("[data-activate-external]").forEach(btn=>{
      btn.addEventListener("click", async () => {
        const recordId = btn.getAttribute("data-activate-external");
        const result = await ctx.catalogRepository.setActiveExternalSource(recordId);
        view.msg.external = result.ok ? "" : result.error;
        await selectPlayer(view.selectedPlayerId);
      });
    });
  }

  paint();
}

// Round alpha.7 (A7-P0-2): unico percorso di errore per il caricamento iniziale — mai composito
// con renderShell()/data-catalog-state="ready", che vive in un ramo return separato sopra.
function renderCatalogLoadError(e){
  return (
    '<div class="catalog" data-catalog-state="error">' +
      '<div class="flex between">' +
        "<h1>Catalogo &amp; statistiche</h1>" +
        '<a href="#/dashboard" id="catalogBackLink">← Le mie aste</a>' +
      "</div>" +
      '<p class="errtext">Impossibile caricare il catalogo: ' + escapeHtml(String(e && e.message || e)) + "</p>" +
    "</div>"
  );
}

// Round Beta-P3-4: percorso DISTINTO da renderCatalogLoadError() sopra — un'incertezza sulla
// sessione, mai "sessione scaduta"/"disconnesso"/"account non valido" (non provate dalla sola
// incertezza di lettura). Retry = richiama per intero renderCatalogPage(), stesso principio già
// stabilito da DashboardPage.js/AuctionDetailPage.js per lo stesso identico tipo di incertezza.
function renderCatalogAuthUncertain(){
  return (
    '<div class="catalog" data-catalog-state="error">' +
      '<div class="card" data-catalog-auth-uncertain>' +
        "<h3>Impossibile verificare la sessione</h3>" +
        '<p class="errtext">Non è stato possibile verificare la tua sessione. Riprova.</p>' +
        '<button type="button" class="btn ghost sm" data-catalog-auth-uncertain-retry>Riprova</button>' +
      "</div>" +
    "</div>"
  );
}
// Round Beta-P3-7 (design D/D2, accettato): UNICO punto da cui ogni retry di questa pagina richiama
// il render completo. Cattura hash+generazione SUBITO DOPO l'invocazione, mai prima (P3-7D2 §5-6:
// un valore catturato prima verrebbe invalidato dall'incremento di QUESTO STESSO tentativo). Il log
// diagnostico avviene SEMPRE, anche per un tentativo ormai stale — solo la scrittura del fallback è
// condizionata al possesso ancora attuale della route.
function attemptCatalogReload(container, ctx, operation){
  return async () => {
    const hashAtRetryStart = window.location.hash;
    const renderPromise = renderCatalogPage(container, ctx);
    const attemptedGeneration = _catalogRenderGeneration;
    try{
      await renderPromise;
    }catch(e){
      // Round Beta-P3-5: log diagnostico una tantum per un imprevisto realmente eccezionale, mai
      // un cambio di controllo/flusso.
      logClientError({ area:"catalog", operation, error:e });
      const stale = _catalogRenderGeneration !== attemptedGeneration || window.location.hash !== hashAtRetryStart;
      if(stale) return;
      renderCatalogSecondaryFallback(container, ctx, operation);
    }
  };
}

// Round Beta-P3-7: card GENERICA per un fallimento inatteso del render completo di retry — mai
// renderCatalogLoadError()/renderCatalogAuthUncertain() sopra, che restano invariate per il loro
// esito già gestito. Il suo stesso pulsante richiama di nuovo lo stesso helper — riarmato
// indefinitamente, mai un pulsante disabilitato in modo permanente.
function renderCatalogSecondaryFallback(container, ctx, operation){
  container.innerHTML =
    '<div class="catalog" data-catalog-state="error">' +
      '<div class="card" data-catalog-secondary-fallback>' +
        "<h3>Impossibile ricaricare</h3>" +
        '<p class="errtext">Non è stato possibile ricaricare il catalogo. Riprova.</p>' +
        '<button type="button" class="btn ghost sm" data-catalog-secondary-retry>Riprova</button>' +
      "</div>" +
    "</div>";
  const retryBtn = container.querySelector("[data-catalog-secondary-retry]");
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptCatalogReload(container, ctx, operation)();
  });
}

function wireCatalogAuthUncertain(container, ctx){
  const retryBtn = container.querySelector("[data-catalog-auth-uncertain-retry]");
  if(!retryBtn) return;
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptCatalogReload(container, ctx, "auth-uncertain-retry")();
  });
}

// Esportata (round 2.2.0), stesso principio di renderListoneSummary/renderStatsSummary sotto:
// esclusivamente per essere testabile in isolamento come funzione pura (verifica che
// view.isAdmin=false non renderizzi mai la sezione conflitti statistici, admin-only) — mai per un
// uso al di fuori del modulo pagina; il chiamante applicativo resta sempre renderCatalogPage()/paint().
export function renderShell(view){
  return (
    '<div class="catalog" data-catalog-state="ready">' +
      '<div class="flex between">' +
        "<h1>Catalogo &amp; statistiche</h1>" +
        '<a href="#/dashboard" id="catalogBackLink">← Le mie aste</a>' +
      "</div>" +
      '<p class="hint">Catalogo Fantacalcio e archivio statistiche multistagione: dato GLOBALE, condiviso da tutte le aste — mai duplicato dentro una singola asta.</p>' +
      renderListoneCard(view) +
      renderStatsCard(view) +
      renderCoverageCard(view) +
      (view.isAdmin ? renderOfficialStatisticsConflictsCard(view) : "") +
      renderNewDepartedCard(view) +
      renderPlayersCard(view) +
      (view.selectedPlayerId ? renderPlayerCard(view) : "") +
    "</div>"
  );
}

function renderListoneCard(view){
  const p = view.listonePreview;
  return (
    '<div class="card">' +
      "<h3>Import / aggiornamento listone</h3>" +
      '<p class="hint">Carica il file Quotazioni (XLSX o CSV): rappresenta l\'INTERO catalogo corrente. Ogni giocatore già presente (per Id Fantacalcio) conserva statistiche e associazioni; i nuovi entrano subito acquistabili; chi non compare più viene segnato come uscito, mai cancellato.</p>' +
      '<input type="file" id="listoneFile" accept=".xlsx,.csv">' +
      (view.msg.listone ? '<p class="errtext">' + escapeHtml(view.msg.listone) + "</p>" : "") +
      (p ? (
        '<div class="card" style="margin-top:10px;">' +
          "<p><strong>" + p.records.length + "</strong> righe valide" + (p.errori ? ", " + p.errori + " in errore" : "") +
            (p.season ? " · stagione rilevata: " + escapeHtml(p.season.label) : " · stagione non rilevata dal nome file") + "</p>" +
          '<div class="flex gap8 wrap">' +
            '<button class="btn primary" id="confirmListoneBtn">Conferma import</button>' +
            '<button class="btn ghost" id="cancelListoneBtn">Annulla</button>' +
          "</div>" +
        "</div>"
      ) : "") +
      (view.lastListoneSummary ? renderListoneSummary(view.lastListoneSummary) : "") +
    "</div>"
  );
}
// Esportata (round alpha.6-r1) esclusivamente per essere testabile in isolamento come funzione
// pura (tests/test_catalog_page_summary_contract_r1.js) — mai per un uso al di fuori del modulo
// pagina; il chiamante applicativo resta sempre renderCatalogPage()/renderListoneCard() sopra.
export function renderListoneSummary(s){
  // Round alpha.6-r1 (correzione contratto real-backend): la RPC import_catalog_snapshot (0008)
  // segnala un reimport identico/idempotente ESCLUSIVAMENTE con skipped:true (mai un campo
  // duplicateFile per il listone, su nessuno dei due backend) — su quel path i conteggi
  // nuovi/aggiornati/invariati/rimossi restituiti sono quelli dell'import ORIGINALE (riletti dalla
  // riga storica), mai quelli di QUESTA chiamata: mostrarli come se fosse un import fresco
  // presenterebbe un'operazione a costo zero come se avesse appena rifatto 504 inserimenti reali.
  if(s.skipped){
    return (
      '<div class="card" style="margin-top:10px;">' +
        "<h3>Ultimo import listone</h3>" +
        '<p class="notice">File già importato in precedenza per questa stagione: nessuna modifica applicata questa volta (idempotenza).</p>' +
      "</div>"
    );
  }
  return (
    '<div class="card" style="margin-top:10px;">' +
      "<h3>Ultimo import listone</h3>" +
      '<div class="flex gap8 wrap">' +
        '<span class="badge ok">Nuovi: ' + s.nuovi + "</span>" +
        '<span class="badge neutral">Aggiornati: ' + s.aggiornati + "</span>" +
        '<span class="badge neutral">Invariati: ' + s.invariati + "</span>" +
        '<span class="badge warn">Usciti: ' + s.rimossi + "</span>" +
      "</div>" +
    "</div>"
  );
}

function renderStatsCard(view){
  const p = view.statsPreview;
  return (
    '<div class="card">' +
      "<h3>Import statistiche stagionali</h3>" +
      '<p class="hint">Carica un file Statistiche (XLSX o CSV) di una stagione specifica: il nome file deve indicare l\'anno (es. "Statistiche_2025_26"). L\'import è un merge non distruttivo — un campo mancante nel nuovo file non cancella mai un valore già presente.</p>' +
      '<input type="file" id="statsFile" accept=".xlsx,.csv">' +
      (view.msg.stats ? '<p class="errtext">' + escapeHtml(view.msg.stats) + "</p>" : "") +
      (p ? (
        '<div class="card" style="margin-top:10px;">' +
          "<p><strong>" + p.records.length + "</strong> righe valide" + (p.errori ? ", " + p.errori + " in errore" : "") + " · stagione: " + escapeHtml(p.season.label) + "</p>" +
          '<div class="flex gap8 wrap">' +
            '<button class="btn primary" id="confirmStatsBtn">Conferma import</button>' +
            '<button class="btn ghost" id="cancelStatsBtn">Annulla</button>' +
          "</div>" +
        "</div>"
      ) : "") +
      (view.lastStatsSummary ? renderStatsSummary(view.lastStatsSummary) : "") +
      '<div class="card" style="margin-top:10px;">' +
        "<h3>Record esterni (manuali, mai scraping automatico)</h3>" +
        '<p class="hint">CSV con colonna fantacalcioId: chi corrisponde a un giocatore corrente e non è ambiguo viene attivato subito; il resto resta in coda.</p>' +
        '<input type="file" id="externalCsvFile" accept=".csv">' +
        (view.msg.external ? '<p class="errtext">' + escapeHtml(view.msg.external) + "</p>" : "") +
      "</div>" +
    "</div>"
  );
}
// Round alpha.6-r1 (correzione contratto real-backend): la RPC import_statistics_archive (0008)
// condivide inseriti/aggiornati/duplicati/conflitti con ArtifactDemoCatalogRepository (stessi nomi
// campo — nessuna divergenza qui), MA sul suo path duplicato/idempotente restituisce
// {skipped:true, reason:'duplicate_file', ...conteggi dell'import ORIGINALE}, MAI duplicateFile —
// controllare solo s.duplicateFile (come prima di questa correzione) lasciava quel path reale
// passare per un import fresco, mostrando conteggi storici come se fossero appena avvenuti. `label`
// invece è un campo puramente Artifact/UI (mai restituito dalla RPC reale, che ha solo seasonKey) —
// escapeHtml(undefined) è già sicuro (mai un'eccezione), ma senza fallback il titolo mostrerebbe
// "()" vuoto: qui si preferisce seasonKey quando label manca, mai un valore inventato.
// Esportata (round alpha.6-r1), stesso motivo di renderListoneSummary sopra.
export function renderStatsSummary(s){
  if(s.skipped || s.duplicateFile){
    return '<div class="card" style="margin-top:10px;"><p class="notice">File già importato in precedenza per questa stagione: nessuna modifica applicata questa volta (idempotenza).</p></div>';
  }
  const titleSeason = s.label || s.seasonKey || "";
  return (
    '<div class="card" style="margin-top:10px;">' +
      "<h3>Ultimo import statistiche" + (titleSeason ? " (" + escapeHtml(titleSeason) + ")" : "") + "</h3>" +
      '<div class="flex gap8 wrap">' +
        '<span class="badge ok">Inseriti: ' + s.inseriti + "</span>" +
        '<span class="badge neutral">Aggiornati: ' + s.aggiornati + "</span>" +
        '<span class="badge neutral">Duplicati: ' + s.duplicati + "</span>" +
        (s.conflitti ? '<span class="badge warn">Conflitti: ' + s.conflitti + "</span>" : "") +
      "</div>" +
    "</div>"
  );
}

function renderCoverageCard(view){
  const c = view.coverage;
  const ex = view.coverageExamples || {};
  return (
    '<div class="card">' +
      '<div class="flex between">' +
        "<h3>Copertura statistiche multistagione</h3>" +
        '<button class="btn ghost sm" id="computeCoverageBtn">Calcola</button>' +
      "</div>" +
      (view.msg.coverage ? '<p class="errtext">' + escapeHtml(view.msg.coverage) + "</p>" : "") +
      (c ? (
        '<div class="flex gap8 wrap">' +
          (c.both != null ? '<span class="badge ok" id="coverageBadgeBoth">In entrambe: ' + c.both + (ex.both ? " (es. " + escapeHtml(ex.both) + ")" : "") + "</span>" : "") +
          Object.keys(c.onlyBySeasons).map(sk => '<span class="badge neutral" data-coverage-only="' + escapeHtml(sk) + '">Solo ' + escapeHtml(sk) + ": " + c.onlyBySeasons[sk] + (ex["only_" + sk] ? " (es. " + escapeHtml(ex["only_" + sk]) + ")" : "") + "</span>").join("") +
          '<span class="badge warn" id="coverageBadgeNone">In nessuna: ' + c.none + (ex.none ? " (es. " + escapeHtml(ex.none) + ")" : "") + "</span>" +
          '<span class="badge warn" id="coverageBadgeDeficiencies">Carenze giocatore-stagione: ' + c.deficienciesCount + "</span>" +
        "</div>"
      ) : '<p class="hint">Premi "Calcola" per la copertura sulle stagioni storiche rilevanti (le due precedenti a quella corrente).</p>') +
    "</div>"
  );
}

// Round 2.2.0 (risoluzione dei conflitti statistici UFFICIALI). Admin-only (vedi view.isAdmin sopra
// — SOLO comodità UI, mai il confine di sicurezza reale). Mostra solo conflitti status='open': un
// conflitto risolto/ignorato scompare da questa lista al refresh successivo (listOfficialStatisticsConflicts
// filtra già status='open' su entrambi i backend), mai lasciato visibile come ancora azionabile.
// external_statistics_conflicts non ha mai una UI qui — esplicitamente fuori scopo per il round 2.2.0.
function renderOfficialStatisticsConflictsCard(view){
  const conflicts = view.officialStatisticsConflicts || [];
  return (
    '<div class="card" data-official-conflicts-card="true">' +
      "<h3>Conflitti statistici ufficiali (admin)</h3>" +
      '<p class="hint">Un conflitto nasce quando un valore già servito e un valore in arrivo sono entrambi reali e diversi: il valore esistente resta attivo finché non viene deciso qui esplicitamente. "Ignora" chiude solo questo conflitto, senza mai modificare il dato servito — un import futuro può generare di nuovo lo stesso conflitto.</p>' +
      (view.msg.conflicts ? '<p class="errtext" id="conflictsMsg">' + escapeHtml(view.msg.conflicts) + "</p>" : "") +
      (conflicts.length === 0
        ? '<p class="hint">Nessun conflitto aperto.</p>'
        : '<div class="table-wrap"><table><thead><tr>' +
            "<th>Giocatore</th><th>Stagione</th><th>Campo</th><th>Valore attuale</th><th>Valore in arrivo</th><th>Fonte</th><th>Azioni</th>" +
          "</tr></thead><tbody>" +
          conflicts.map(c => (
            '<tr data-conflict-row="' + escapeHtml(c.id) + '">' +
              "<td>" + escapeHtml(String(c.fantacalcioId)) + "</td>" +
              "<td>" + escapeHtml(c.seasonKey) + "</td>" +
              "<td>" + escapeHtml(c.field) + "</td>" +
              "<td>" + escapeHtml(c.existingValue == null ? "" : String(c.existingValue)) + "</td>" +
              "<td>" + escapeHtml(c.incomingValue == null ? "" : String(c.incomingValue)) + "</td>" +
              "<td>" + escapeHtml(c.sourceFile || "") + "</td>" +
              "<td>" +
                '<div class="flex gap8 wrap">' +
                  '<button class="btn ghost sm" data-conflict-action="keep_current" data-conflict-id="' + escapeHtml(c.id) + '">Mantieni attuale</button>' +
                  '<button class="btn ghost sm" data-conflict-action="accept_incoming" data-conflict-id="' + escapeHtml(c.id) + '">Accetta in arrivo</button>' +
                  '<input type="text" class="sm" style="width:90px" data-conflict-custom-input="' + escapeHtml(c.id) + '" placeholder="valore">' +
                  '<button class="btn ghost sm" data-conflict-action="custom_value" data-conflict-id="' + escapeHtml(c.id) + '">Valore personalizzato</button>' +
                  '<button class="btn ghost sm" data-conflict-action="ignore" data-conflict-id="' + escapeHtml(c.id) + '">Ignora</button>' +
                "</div>" +
              "</td>" +
            "</tr>"
          )).join("") +
          "</tbody></table></div>"
      ) +
    "</div>"
  );
}

// Round alpha.6-r1 (correzione contratto real-backend, PROD PostMortem): nuoviPlayerIds/
// removedPlayers/autoLinked sono capacità OPZIONALI del solo ArtifactDemoCatalogRepository
// (catalogSnapshotEngine.js) — la RPC reale import_catalog_snapshot (0008) non le ha MAI restituite
// (né mai le restituirà: derivarle qui via query pre/post-import introdurrebbe una falsa
// equivalenza/race, vedi ALPHA6-R1 P0 audit). Il conteggio nuovi/aggiornati/invariati/rimossi
// (renderListoneSummary, sempre presente su entrambi i backend) resta l'unica fonte di verità
// business-corretta; questa card è puro dettaglio UI e deve degradare pulitamente, mai fabbricare
// un elenco quando il backend non lo fornisce, mai lanciare un'eccezione che spezza l'intero render.
export function renderNewDepartedCard(view){
  const s = view.lastListoneSummary;
  if(!s || s.skipped) return "";
  const hasNuoviDetail = Array.isArray(s.nuoviPlayerIds);
  const hasRemovedDetail = Array.isArray(s.removedPlayers);
  if(!hasNuoviDetail && !hasRemovedDetail) return "";
  const autoLinked = Array.isArray(s.autoLinked) ? s.autoLinked : [];
  const nuoviSection = !hasNuoviDetail ? "" : (() => {
    const nuovi = view.players.filter(p => s.nuoviPlayerIds.includes(p.id));
    return '<div style="flex:1;"><p class="hint">Nuovi (' + nuovi.length + ")</p><ul>" +
      nuovi.map(p => "<li>" + escapeHtml(p.nome) + " (" + escapeHtml(p.squadra) + ")" + (autoLinked.find(a=>a.playerId===p.id && a.linkedSeasons.length) ? ' <span class="badge ok">storico trovato</span>' : "") + "</li>").join("") +
      "</ul></div>";
  })();
  const removedSection = !hasRemovedDetail ? "" : (
    '<div style="flex:1;"><p class="hint">Usciti (' + s.removedPlayers.length + ")</p><ul>" +
      s.removedPlayers.map(p => "<li>" + escapeHtml(p.nome) + " (" + escapeHtml(p.squadra) + ")</li>").join("") +
    "</ul></div>"
  );
  return (
    '<div class="card">' +
      "<h3>Nuovi / usciti dall'ultimo import listone</h3>" +
      '<div class="flex gap8" style="align-items:flex-start;">' +
        nuoviSection + removedSection +
      "</div>" +
    "</div>"
  );
}

// Round A7-P1-4 — unico punto di composizione fra ricerca testuale e filtro ruolo (mai due filtri
// applicati separatamente in punti diversi, che potrebbero divergere). Pura: non muta mai l'array
// players in ingresso (Array.prototype.filter restituisce sempre un nuovo array), preserva l'ordine
// di sorgente per costruzione (FILTER_PRESERVES_SOURCE_ORDER=YES — filter() non riordina mai).
// Esportata per essere testabile in isolamento (stesso motivo di renderListoneSummary ecc. sotto).
export function filterCatalogPlayers(players, { search, role } = {}){
  const term = (search || "").trim().toLowerCase();
  const roleValue = role || "ALL";
  return players.filter(p => {
    if(roleValue !== "ALL" && p.ruolo !== roleValue) return false;
    if(term && !(p.nome.toLowerCase().includes(term) || p.squadra.toLowerCase().includes(term))) return false;
    return true;
  });
}

// data-catalog-role-filter resta un marcatore booleano (mai ="true", stesso stile di
// data-opponent-intelligence in AuctionDetailPage.js) — è la sola sezione, non un valore. `disabled`
// SOLO quando l'intero catalogo è vuoto (nulla da filtrare indipendentemente dal ruolo scelto): in
// quel caso i controlli restano visibili ma inerti, mai nascosti (comportamento scelto e testato,
// A7-P1-4D §18). aria-pressed/data-catalog-role-selected restano coerenti anche da disabilitati —
// esattamente un bottone "true" in ogni momento, indipendentemente dallo stato del catalogo.
function renderRoleFilterGroup(selectedRole, disabled){
  return (
    '<div class="flex gap8 wrap" role="group" aria-label="Filtra per ruolo" data-catalog-role-filter>' +
      ROLE_FILTER_OPTIONS.map(role => {
        const selected = role === selectedRole;
        const cls = selected ? "btn primary sm" : "btn ghost sm";
        return '<button type="button" class="' + cls + '" data-catalog-role="' + role + '" data-catalog-role-selected="' + (selected ? "true" : "false") + '" aria-pressed="' + (selected ? "true" : "false") + '"' + (disabled ? " disabled" : "") + '>' +
          escapeHtml(ROLE_FILTER_LABELS[role]) +
        "</button>";
      }).join("") +
    "</div>"
  );
}

function renderPlayersCard(view){
  const filtered = filterCatalogPlayers(view.players, { search: view.playerFilter, role: view.roleFilter });
  // A7-P1-4 amendment 1: matchCount/renderedCount/hiddenCount restano tre grandezze DISTINTE, mai
  // confuse sotto un'unica "visible". Round Beta-P2-1: il cap fisso a 50 righe è sostituito dalla
  // finestra di stato view.catalogVisibleCount — Array.slice() resta l'UNICO meccanismo di
  // paginazione, sempre sullo stesso array `filtered` (mai un nuovo fetch/ordine): nessun duplicato,
  // nessuna riga mancante sono possibili per costruzione, qualunque sia l'ampiezza della finestra.
  const matchCount = filtered.length;
  const shown = filtered.slice(0, view.catalogVisibleCount);
  const renderedCount = shown.length;
  const hiddenCount = Math.max(matchCount - renderedCount, 0);
  const catalogEmpty = view.players.length === 0;
  const playersState = catalogEmpty ? "empty-catalog" : (matchCount === 0 ? "empty-filtered" : "populated");
  return (
    '<div class="card" data-catalog-players-state="' + playersState + '" data-catalog-match-count="' + matchCount + '" data-catalog-rendered-count="' + renderedCount + '">' +
      '<div class="flex between">' +
        "<h3>Catalogo corrente (" + matchCount + " giocatori)</h3>" +
        '<input type="text" id="playerFilter" placeholder="Cerca nome/squadra…" value="' + escapeHtml(view.playerFilter) + '">' +
      "</div>" +
      renderRoleFilterGroup(view.roleFilter, catalogEmpty) +
      (playersState === "empty-catalog" ? '<p class="hint">Nessun giocatore ancora: importa un listone qui sopra.</p>' :
       playersState === "empty-filtered" ? (
        '<p class="hint">Nessun giocatore corrisponde ai filtri attuali — prova a modificare ruolo o ricerca. ' +
          '<button type="button" class="btn ghost sm" id="resetCatalogFiltersBtn">Azzera filtri</button></p>'
       ) :
        '<div class="table-wrap"><table><thead><tr><th>Nome</th><th>Squadra</th><th>Ruolo</th><th>Qt.A</th><th>FVM</th><th></th></tr></thead><tbody>' +
          shown.map(p =>
            "<tr>" +
              "<td>" + escapeHtml(p.nome) + "</td>" +
              "<td>" + escapeHtml(p.squadra) + "</td>" +
              "<td>" + escapeHtml(p.ruolo) + "</td>" +
              "<td>" + p.quotazione + "</td>" +
              "<td>" + p.fvm + "</td>" +
              '<td><button class="btn ghost sm" data-select-player="' + escapeHtml(p.id) + '">Scheda</button></td>' +
            "</tr>"
          ).join("") +
        "</tbody></table></div>" +
        // Round Beta-P2-1: un'azione di browse esplicita ("Load more") sostituisce il vecchio
        // suggerimento "affina la ricerca" — quel testo non deve mai restare quando un'azione di
        // browse è realmente disponibile (la ricerca resta comunque utilizzabile, ma non è più
        // l'UNICO modo per raggiungere le righe nascoste).
        (hiddenCount > 0
          ? '<p class="hint">' + renderedCount + " di " + matchCount + ' giocatori mostrati. ' +
            '<button type="button" class="btn ghost sm" data-catalog-load-more>Mostra altri ' +
              Math.min(CATALOG_PAGE_SIZE, hiddenCount) + "</button></p>"
          : "")
      ) +
    "</div>"
  );
}

// Revisione r3 (audit, blocker 3): data-external-stats espone i valori numerici cumulativi come
// coppie chiave=valore (mai visibili nel testo del badge, per non appesantire la UI) — necessario
// perché il test browser deve poter verificare i valori ESATTI dopo un aggiornamento, non solo che
// esista un record (un conteggio invariato avrebbe potuto nascondere valori rimasti stantii).
function externalStatsDataAttr(r){
  const fields = ["appearances","starts","minutes","goals","assists","yellowCards","redCards","penaltiesScored","penaltiesMissed","cleanSheets","goalsConceded"];
  return fields.map(f => f + "=" + (r[f]==null ? "" : r[f])).join(";");
}
function renderExternalStatsList(records){
  if(!records.length) return '<p class="hint">Nessun record esterno risolto per questo giocatore.</p>';
  return (
    '<div class="flex gap8 wrap" style="margin-bottom:10px;">' +
      records.map(r =>
        '<span class="badge ' + (r.active ? "ok" : "neutral") + '" data-external-stats="' + escapeHtml(externalStatsDataAttr(r)) + '">' +
          escapeHtml(r.source) + " · " + escapeHtml(r.season) + " · " + escapeHtml(r.sourcePlayerId) +
          (r.active ? " (attivo)" : "") +
          (!r.active ? ' <button class="btn ghost sm" data-activate-external="' + escapeHtml(r.id) + '" style="margin-left:6px;">Rendi attiva</button>' : "") +
        "</span>"
      ).join("") +
    "</div>"
  );
}

function renderQueueStatsList(records){
  if(!records.length) return "";
  return (
    '<div class="flex gap8 wrap" style="margin-bottom:10px;" id="externalQueueList">' +
      records.map(r =>
        '<span class="badge warn" data-queue-status="' + escapeHtml(r.status) + '">' +
          escapeHtml(r.status) + ": " + escapeHtml(r.source) + " · " + escapeHtml(r.sourcePlayerId||"") +
          (r.reason ? " — " + escapeHtml(r.reason) : "") +
        "</span>"
      ).join("") +
    "</div>"
  );
}

function renderPlayerCard(view){
  const p = view.selectedPlayerDetail;
  if(!p) return "";
  const externalSources = EXTERNAL_SOURCES.map(s => '<option value="' + s.value + '">' + escapeHtml(s.label) + "</option>").join("");
  return (
    '<div class="card">' +
      '<div class="flex between">' +
        "<h3>Scheda giocatore — " + escapeHtml(p.nome) + "</h3>" +
        '<button class="btn ghost sm" id="closePlayerCardBtn">Chiudi</button>' +
      "</div>" +
      "<p>" + escapeHtml(p.squadra) + " · " + escapeHtml(p.ruolo) + (p.ruoloMantra ? " / " + escapeHtml(p.ruoloMantra) : "") +
        " · Qt.A " + p.quotazione + " · FVM " + p.fvm + " · Id Fantacalcio " + p.fantacalcioId +
      "</p>" +
      "<h3>Statistiche per stagione</h3>" +
      '<div class="table-wrap"><table><thead><tr><th>Stagione</th><th>Pv</th><th>Mv</th><th>Fm</th><th>Gf</th><th>Gs</th><th>Ass</th></tr></thead><tbody>' +
        Object.keys(view.selectedPlayerStats).map(sk => {
          const st = view.selectedPlayerStats[sk];
          return "<tr><td>" + escapeHtml(sk) + "</td>" +
            (st ? ("<td>" + (st.pv??"—") + "</td><td>" + (st.mv??"—") + "</td><td>" + (st.fm??"—") + "</td><td>" + (st.gf??"—") + "</td><td>" + (st.gs??"—") + "</td><td>" + (st.assist??"—") + "</td>")
                : '<td colspan="6" class="hint">Nessun dato ufficiale per questa stagione</td>') +
          "</tr>";
        }).join("") +
      "</tbody></table></div>" +
      "<h3>Record esterni risolti (statistiche, mai voti/Mv/Fm ufficiali)</h3>" +
      (view.msg.external ? '<p class="errtext">' + escapeHtml(view.msg.external) + "</p>" : "") +
      renderExternalStatsList(view.selectedPlayerExternalStats||[]) +
      renderQueueStatsList(view.selectedPlayerQueueStats||[]) +
      "<h3>Associazioni esterne (manuali)</h3>" +
      '<div class="flex gap8 wrap">' +
        // Revisione alpha.3-r3 (audit blocker B su r2): una fonte vendor-scoped ("provider-authorized")
        // può avere PIÙ associazioni contemporanee, una per vendor — externalIdEntries() le appiattisce
        // tutte (comprese quelle legacy scalari), ognuna col proprio pulsante di rimozione che porta
        // con sé anche il providerKey (mai solo la fonte, altrimenti rimuoverebbe la associazione
        // vendor sbagliata quando più di una coesiste).
        (externalIdEntries(p).length === 0 ? '<span class="hint">Nessuna associazione esterna.</span>' :
          externalIdEntries(p).map(entry =>
            '<span class="badge neutral">' + escapeHtml(entry.source) +
              (entry.providerKey ? " (" + escapeHtml(entry.providerKey) + ")" : "") +
              ": " + escapeHtml(entry.value) +
              ' <button class="btn ghost sm" data-remove-external="' + escapeHtml(entry.source) + '"' +
              (entry.providerKey ? ' data-remove-external-provider-key="' + escapeHtml(entry.providerKey) + '"' : '') +
              ' style="margin-left:6px;">✕</button></span>'
          ).join("")
        ) +
      "</div>" +
      '<form id="associateExternalForm" class="flex gap8 wrap" style="margin-top:8px;">' +
        '<select id="associateSource">' + externalSources + "</select>" +
        '<input type="text" id="associateProviderKey" placeholder="Provider key (solo per fonti vendor-scoped)">' +
        '<input type="text" id="associateSourceId" placeholder="Identificativo sulla fonte" required>' +
        '<input type="text" id="associateSourceUrl" placeholder="URL (opzionale)">' +
        '<button type="submit" class="btn ghost sm">Associa</button>' +
      "</form>" +
    "</div>"
  );
}
